import { useMemo } from 'react';
import { CalloutPlacementDiscriminant } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc';
import { useHeroData } from '../hooks/useHeroData';

/**
 * MKTD-808 — temporary mock of the `getPlacements` response.
 *
 * The studio-owned `PlacementsRpc.getPlacements` is not yet returning the data
 * we need for the Workflows Phase 2 release, so we source callout placements
 * from the existing `useHeroData` config instead. This mock mirrors the exact
 * `GetPlacementsResponse` shape ({ placements, total }) so that swapping back to
 * the real RPC (see `useRpcPagePlacements` in `usePagePlacements`) is a one-line
 * change once the backend is ready.
 *
 * Layout mapping (per page, in hero-carousel order):
 *   - hero[0]      -> TOP_HERO   (rendered as the 2/3-width hero)
 *   - hero[1]      -> TOP_CARD   (rendered as the 1/3-width card)
 *   - hero[2..n]   -> BANNER_1, BANNER_2, ... (rendered as full-width banners)
 */

const MOCK_AUDIENCE = 'ALL';
const MOCK_UPDATED_BY = 'mock';
const MOCK_UPDATED_AT = 0;
const MOCK_ENTITY_ID_BASE = 900000000;
function sectionForIndex(index) {
  if (index === 0) {
    return 'TOP_HERO';
  }
  if (index === 1) {
    return 'TOP_CARD';
  }
  return `BANNER_${index - 1}`;
}
function heroItemToCalloutPlacement(item, page, index) {
  var _item$resultType;
  const entityId = MOCK_ENTITY_ID_BASE + index;
  const placement = {
    entityType: CalloutPlacementDiscriminant,
    page,
    section: sectionForIndex(index),
    entityId,
    name: item.title,
    publishState: 'PUBLISHED',
    contentChanged: false,
    calloutContent: {
      title: item.title,
      description: item.subtitle,
      buttonText: item.buttonText,
      buttonUrl: item.buttonUrl,
      resultType: (_item$resultType = item.resultType) !== null && _item$resultType !== void 0 ? _item$resultType : 'EXTERNAL_LINK',
      imageUrl: item.imageUrl,
      videoId: item.videoId,
      listingId: item.listingId,
      entityVersionId: entityId,
      audience: MOCK_AUDIENCE,
      updatedAt: MOCK_UPDATED_AT,
      updatedBy: MOCK_UPDATED_BY,
      // Legacy hero CTA behaviors that aren't part of the callout model yet
      // (MKTD-808). Preserved so the mock renders CTAs exactly as the legacy
      // hero did — copilot open, Find-a-partner modal, secondary button.
      openCopilot: item.openCopilot,
      openFindPartnerMatch: item.openFindPartnerMatch,
      secondaryButtonText: item.secondaryButtonText,
      secondaryButtonUrl: item.secondaryButtonUrl
    }
  };
  return placement;
}

/**
 * Builds a mock `getPlacements` response for the current route from
 * `useHeroData`. Returns an empty response when the page is unknown or has no
 * hero content.
 */
export function useMockPlacementsResponse(page) {
  const {
    heroItems
  } = useHeroData();
  return useMemo(() => {
    if (!page) {
      return {
        placements: [],
        total: 0
      };
    }
    const placements = heroItems.map((item, index) => heroItemToCalloutPlacement(item, page, index));
    return {
      placements,
      total: placements.length
    };
  }, [heroItems, page]);
}